import { Collapse, Input, InputNumber, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { SlaFormField } from '~/app/landing/sla/shared/components/SlaFormField';
import type {
  MaintenanceContractIssue,
  MaintenanceSlaDraftLevel,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { MaintenanceSlaTimerEditor } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaTimerEditor/MaintenanceSlaTimerEditor';
import { MaintenanceSlaPricingCombinationsEditor } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaPricingCombinationsEditor/MaintenanceSlaPricingCombinationsEditor';
import {
  levelCardBodyStyle,
  levelCardClockHeaderStyle,
  levelCardClockSectionStyle,
  levelCardCodeStyle,
  levelCardDefinitionStyle,
  levelCardExamplesStyle,
  levelCardFieldItemStyle,
  levelCardFieldsStyle,
  levelCardHeaderStyle,
  levelCardOptionalStyle,
  levelCardPricingCountStyle,
  levelCardSharedBadgeStyle,
  levelCardStyle,
  levelCardTitleStyle,
} from './MaintenanceSlaLevelCard.style';

export interface MaintenanceSlaLevelCardProps {
  level: MaintenanceSlaDraftLevel;
  issues: readonly MaintenanceContractIssue[];
  onChange: (level: MaintenanceSlaDraftLevel) => void;
  defaultOpen?: boolean;
  currency?: string;
}

export function MaintenanceSlaLevelCard({
  level,
  issues,
  onChange,
  defaultOpen = false,
  currency = 'EUR',
}: MaintenanceSlaLevelCardProps) {
  const { t } = useTranslation('sla');
  const pricingCount = level.pricingCombinations.length;

  return (
    <Collapse
      defaultActiveKey={defaultOpen ? [level.code] : []}
      ghost={false}
      size="small"
      style={levelCardStyle}
      items={[
        {
          key: level.code,
          label: (
            <span style={levelCardHeaderStyle}>
              <Tag style={levelCardCodeStyle}>{level.code}</Tag>
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={levelCardTitleStyle}>
                  {t('oems.maintenance.levelLabel', { code: level.code })}
                </span>
                <span style={levelCardDefinitionStyle}>
                  {t(`oems.maintenance.levelGuide.${level.code}.definition`)}
                </span>
                <span style={levelCardExamplesStyle}>
                  {t('oems.maintenance.examplesPrefix')}{' '}
                  {t(`oems.maintenance.levelGuide.${level.code}.examples`)}
                </span>
              </span>
              <span style={levelCardPricingCountStyle}>
                {t('oems.maintenance.pricingOptionsCount', {
                  count: pricingCount,
                })}
              </span>
            </span>
          ),
          children: (
            <div style={levelCardBodyStyle}>
              <div style={levelCardFieldsStyle}>
                <div style={levelCardFieldItemStyle}>
                  <SlaFormField
                    label={t('oems.maintenance.penaltyAmountPer24h')}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      value={level.penaltyAmount}
                      addonAfter={currency}
                      onChange={(penaltyAmount) =>
                        onChange({ ...level, penaltyAmount })
                      }
                    />
                  </SlaFormField>
                </div>
                <div style={levelCardFieldItemStyle}>
                  <SlaFormField
                    label={
                      <span>
                        {t('oems.maintenance.penaltyClause')}
                        <span style={levelCardOptionalStyle}>
                          {t('oems.maintenance.optional')}
                        </span>
                      </span>
                    }
                  >
                    <Input
                      placeholder={t(
                        'oems.maintenance.penaltyClausePlaceholder',
                      )}
                      value={level.penaltyClauseText}
                      onChange={(event) =>
                        onChange({
                          ...level,
                          penaltyClauseText: event.target.value,
                        })
                      }
                    />
                  </SlaFormField>
                </div>
              </div>
              <MaintenanceSlaPricingCombinationsEditor
                level={level}
                issues={issues}
                onChange={onChange}
              />
              <div style={levelCardClockSectionStyle}>
                <div style={levelCardClockHeaderStyle}>
                  <Typography.Text strong>
                    {t('oems.maintenance.clockSettings')}
                  </Typography.Text>
                  <Tag style={levelCardSharedBadgeStyle}>
                    {t('oems.maintenance.sharedAcrossPricingCombinations')}
                  </Tag>
                </div>
                <MaintenanceSlaTimerEditor
                  level={level}
                  issues={issues}
                  onChange={onChange}
                />
              </div>
            </div>
          ),
        },
      ]}
    />
  );
}
